import { toMerged } from 'es-toolkit'
import { z } from 'zod'

import type { LeadAssignmentValidationMessages } from './types'

import { clientTypes } from '../../client-type'
import { countriesList } from '../../countries'
import { supportedLocales } from '../../supported-locales'
import { whitelabelsList } from '../../whitelabels'
import { defaultMessages } from './messages'

/**
 * Creates a Zod schema for validating lead assignment workflow configurations.
 *
 * The function accepts optional custom validation error messages that are merged
 * with default messages. Returns a Zod schema that validates a workflow object
 * with fields: events, pools, forceful, name, rules, whitelabel.
 *
 * @param messages - Optional custom validation error messages.
 *   If not provided, default messages are used.
 * @returns Zod schema for validating lead assignment workflow.
 *
 * @example
 * ```ts
 * const schema = createLeadAssignmentSchemas({
 *   name: { required: 'Name is required' }
 * });
 * const result = schema.parse(workflowData);
 * ```
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function createLeadAssignmentSchemas(messages: LeadAssignmentValidationMessages = {}) {
  const msg = toMerged(defaultMessages, messages)

  const eventValueSchema = z.object({
    delayInSeconds: z
      .number({
        error: msg.delayInSeconds.invalid_type,
      })
      .optional(),
  })

  const eventMapSchema = z.object(
    {
      AssignToAffiliate: eventValueSchema.optional(),
      SignUp: eventValueSchema.optional(),
    },
    {
      error: (iss: { input?: unknown }) => (iss.input === undefined ? msg.events.required : msg.events.invalid_type),
    },
  )

  const baseLeadAssignmentRuleSchema = z.object({
    isInverted: z
      .boolean({
        error: msg.isInverted.invalid_type,
      })
      .optional(),
    type: z.string({
      error: (iss) => (iss.input === undefined ? msg.type.required : msg.type.invalid_type),
    }),
  })

  const affiliateLeadAssignmentRuleSchema = baseLeadAssignmentRuleSchema.extend({
    affiliateCxIds: z
      .array(z.number(), {
        error: (iss) => (iss.input === undefined ? msg.affiliateCxIds.required : msg.affiliateCxIds.invalid_type),
      })
      .min(1, {
        error: msg.affiliateCxIds.nonempty,
      }),
    includeSubAffiliates: z.coerce.boolean<boolean>({
      error: (iss) =>
        iss.input === undefined ? msg.includeSubAffiliates.required : msg.includeSubAffiliates.invalid_type,
    }),
    type: z.literal('Affiliate', {
      error: msg.type.invalid_literal,
    }),
  })

  const campaignLeadAssignmentRuleSchema = baseLeadAssignmentRuleSchema.extend({
    campaignIds: z
      .array(z.number(), {
        error: (iss) => (iss.input === undefined ? msg.campaignIds.required : msg.campaignIds.invalid_type),
      })
      .min(1, {
        error: msg.campaignIds.nonempty,
      }),
    type: z.literal('Campaign', {
      error: msg.type.invalid_literal,
    }),
  })

  const clientLocaleLeadAssignmentRuleSchema = baseLeadAssignmentRuleSchema.extend({
    locales: z.array(z.enum(supportedLocales), {
      error: (iss) => (iss.input === undefined ? msg.locales.required : msg.locales.invalid_type),
    }),
    type: z.literal('ClientLocaleLeadAssignmentRule', {
      error: msg.type.invalid_literal,
    }),
  })

  const clientTypeLeadAssignmentRuleSchema = baseLeadAssignmentRuleSchema.extend({
    clientTypes: z.array(z.enum(clientTypes), {
      error: (iss) => (iss.input === undefined ? msg.clientTypes.required : msg.clientTypes.invalid_type),
    }),
    type: z.literal('ClientType', {
      error: msg.type.invalid_literal,
    }),
  })

  const firstTimeDepositLeadAssignmentRuleSchema = baseLeadAssignmentRuleSchema.extend({
    type: z.literal('FirstTimeDeposit', {
      error: msg.type.invalid_literal,
    }),
  })

  const ibClientLeadAssignmentRuleSchema = baseLeadAssignmentRuleSchema.extend({
    ibIds: z
      .array(z.number(), {
        error: (iss) => (iss.input === undefined ? msg.ibIds.required : msg.ibIds.invalid_type),
      })
      .min(1, {
        error: msg.ibIds.nonempty,
      }),
    includingSubIBs: z.coerce.boolean<boolean>({
      error: (iss) => (iss.input === undefined ? msg.includingSubIBs.required : msg.includingSubIBs.invalid_type),
    }),
    type: z.literal('IBClient', {
      error: msg.type.invalid_literal,
    }),
  })

  const registrationCountryLeadAssignmentRuleSchema = baseLeadAssignmentRuleSchema.extend({
    countries: z.array(z.enum(countriesList), {
      error: (iss) => (iss.input === undefined ? msg.countries.required : msg.countries.invalid_type),
    }),
    type: z.literal('RegistrationCountry', {
      error: msg.type.invalid_literal,
    }),
  })

  const leadAssignmentRuleSchema = z.discriminatedUnion('type', [
    affiliateLeadAssignmentRuleSchema,
    campaignLeadAssignmentRuleSchema,
    clientLocaleLeadAssignmentRuleSchema,
    clientTypeLeadAssignmentRuleSchema,
    firstTimeDepositLeadAssignmentRuleSchema,
    ibClientLeadAssignmentRuleSchema,
    registrationCountryLeadAssignmentRuleSchema,
  ])

  const workflowPoolsSchema = z.object({
    externalAgent: z
      .string({
        error: msg.externalAgent.invalid_type,
      })
      .transform((val) => (val === '' ? undefined : val))
      .optional(),
    salesAgent: z
      .string({
        error: msg.salesAgent.invalid_type,
      })
      .transform((val) => (val === '' ? undefined : val))
      .optional(),
    supportAgent: z
      .string({
        error: msg.supportAgent.invalid_type,
      })
      .transform((val) => (val === '' ? undefined : val))
      .optional(),
  })

  return z.object({
    events: eventMapSchema,
    pools: workflowPoolsSchema,
    forceful: z
      .boolean({
        error: msg.forceful.invalid_type,
      })
      .optional(),
    name: z.string({
      error: (iss) => (iss.input === undefined ? msg.name.required : msg.name.invalid_type),
    }),
    rules: z.array(z.array(leadAssignmentRuleSchema), {
      error: (iss) => (iss.input === undefined ? msg.rules.required : msg.rules.invalid_type),
    }),
    whitelabel: z.enum(whitelabelsList, {
      error: createEnumErrorMap(
        {
          invalid_enum: msg.whitelabel.invalid_enum,
          invalid_type: msg.whitelabel.invalid_type,
          required: msg.whitelabel.required,
        },
        true,
      ),
    }),
  })
}

/**
 * Creates a reusable error map for enum fields.
 *
 * @param messages - Object containing error messages for invalid_enum, invalid_type, and optionally required
 * @param isRequired - Whether the field is required (default: false)
 * @returns Zod v4 error function
 */
function createEnumErrorMap(
  messages: {
    invalid_enum: string
    invalid_type: string
    required?: string
  },
  isRequired = false,
): (issue: { code?: string; input?: unknown; received?: string }) => string {
  return (issue) => {
    if (issue.code === 'invalid_type') {
      if (isRequired && issue.received === 'undefined') {
        return messages.required ?? messages.invalid_type
      }
      return messages.invalid_type
    }
    if (issue.code === 'invalid_value') {
      return messages.invalid_enum
    }

    if (isRequired) {
      return messages.required ?? messages.invalid_type
    }
    return messages.invalid_enum
  }
}

/**
 * Zod schema for validating lead assignment workflow configurations.
 *
 * Validates a workflow object containing:
 * - `events`: Event map with delay configurations for AssignToAffiliate and SignUp events
 * - `forceful`: Optional boolean flag for forceful assignment
 * - `name`: Workflow name string
 * - `pools`: Agent pool configuration for external, partner, and sales agents
 * - `rules`: Array of rule groups (each group is an array of lead assignment rules)
 * - `whitelabel`: Whitelabel identifier from the supported whitelabels list
 *
 * @example
 * ```ts
 * const workflow = {
 *   name: "My Workflow",
 *   whitelabel: "example",
 *   pools: {
 *     externalAgent: "pool-1",
 *     supportAgent: "pool-2",
 *     salesAgent: "pool-3"
 *   },
 *   rules: [[{ type: "ClientType", clientTypes: ["Retail"] }]],
 *   events: {
 *     AssignToAffiliate: { delayInSeconds: 10 },
 *     SignUp: {}
 *   }
 * };
 * leadAssignmentWorkflowSchema.parse(workflow);
 * ```
 */
export const leadAssignmentWorkflowSchema = createLeadAssignmentSchemas()
